'use client';

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldCheck } from "lucide-react";


export default function TermsPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <main className="flex-1 flex flex-col items-center justify-center">
        <section className="w-full py-20 px-4 flex flex-col items-center bg-gradient-to-b from-sky-100/60 to-background dark:from-slate-900/80 dark:to-background transition-colors">
          <div className="max-w-3xl w-full">
            <Card className="w-full rounded-2xl shadow-2xl border-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg p-0">
              <div className="flex flex-col items-center gap-2 pt-10">
                <div className="bg-sky-100 dark:bg-sky-900 rounded-full p-4 mb-2">
                  <ShieldCheck className="h-8 w-8 text-sky-700 dark:text-sky-400" />
                </div>
                <h1 className="text-4xl font-extrabold text-sky-700 dark:text-sky-400 text-center tracking-tight mb-2">
                  Terms and Conditions
                </h1>
                <p className="text-base text-muted-foreground text-center max-w-xl mb-6">
                  Please read these terms carefully. By using Flight Deck, you agree to these conditions.
                </p>
              </div>
              <div className="px-8 pb-10 pt-2 space-y-10">
                {/* 1. Definitions */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">1. Definitions</h2>
                  <ul className="list-disc pl-6 space-y-1 text-base">
                    <li>
                      <span className="font-medium text-foreground">Flight Deck:</span> The service offered by Dutch Sim Pilot that logs VATSIM or IVAO flights, awards XP, and generates statistics for users.
                    </li>
                    <li>
                      <span className="font-medium text-foreground">User:</span> Any person who creates an account and uses the services of Flight Deck.
                    </li>
                    <li>
                      <span className="font-medium text-foreground">SSO:</span> Single Sign-On, a login system allowing users to log in via an external party such as VATSIM or IVAO.
                    </li>
                    <li>
                      <span className="font-medium text-foreground">VATSIM CID / IVAO ID:</span> Your unique identification code within the VATSIM or IVAO network, respectively.
                    </li>
                  </ul>
                </section>

                {/* 2. Use of the Service */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">2. Use of the Service</h2>
                  <p>
                    Flight Deck offers a free service where users can log their VATSIM or IVAO flights, earn XP, and generate statistics. The service is intended solely for recreational use within the context of flight simulation. Commercial use or use for real-world aviation purposes is expressly excluded.
                  </p>
                </section>

                {/* 3. Registration and Data */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">3. Registration and Data</h2>
                  <p>
                    To use Flight Deck, you must log in via a supported Single Sign-On (SSO) method, such as VATSIM or IVAO. The following data may be collected and stored:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Name and surname</li>
                    <li>Email address</li>
                    <li>VATSIM CID or IVAO ID</li>
                  </ul>
                  <p>
                    This data is used solely for authentication, tracking your statistics, and user identification. Your flight information may be visible to other users, depending on your privacy settings (public or private account).
                  </p>
                </section>

                {/* 4. Privacy and Data Protection */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">4. Privacy and Data Protection</h2>
                  <p>
                    Flight Deck does not share personal data with third parties. All collected data is used exclusively for the operation and improvement of the service and is securely stored in accordance with applicable privacy laws. For more information, please refer to our Privacy Policy.
                  </p>
                </section>

                {/* 5. Liability */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">5. Liability</h2>
                  <p>
                    While we strive for correct operation of the service, we cannot guarantee the availability, completeness, or accuracy of the data. Flight Deck and Dutch Sim Pilot are not liable for damages resulting from technical failures, data loss, or incorrect information, unless caused by intent or gross negligence. The service is intended solely for entertainment purposes and must not be used for real-world aviation applications.
                  </p>
                </section>

                {/* 6. XP and Rank System */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">6. XP and Rank System</h2>
                  <p>
                    XP (experience points) are awarded based on completed flights on VATSIM or IVAO and may vary depending on the flight and other factors. XP is solely for illustrating your experience and rank on Flight Deck and has no real-world value or benefits. The rank is displayed in a system similar to aviation ranks (Second Officer, First Officer, Captain, etc.) and is purely for illustration of your experience.
                  </p>
                </section>

                {/* 7. Changes to the Terms */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">7. Changes to the Terms</h2>
                  <p>
                    Dutch Sim Pilot reserves the right to change these terms and conditions at any time. Changes will be announced on the website and take effect upon publication.
                  </p>
                </section>

                {/* 8. Contact */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">8. Contact</h2>
                  <p>
                    For questions or comments about these terms and conditions, you can contact us via the Dutch Sim Pilot Discord or by email at <a href="mailto:info@flightdeck.me" className="underline text-sky-700 dark:text-sky-400">info@flightdeck.me</a>.
                  </p>
                </section>

                <section>
                  <p className="text-muted-foreground text-sm mt-8">
                    By using Flight Deck, you confirm that you have read and understood these terms and conditions and agree to be bound by them.
                  </p>
                </section>
              </div>
            </Card>
            <div className="mt-10 flex justify-center">
              <Link href="/">
                <Button
                  variant="outline"
                  className="text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-800 hover:bg-sky-50 dark:hover:bg-sky-900/40 transition"
                >
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}